import moment from "moment-timezone";
import { Label } from "north.js";
import React from "react";
import styled from "styled-components";

import { LiveIcon, LocationMarker } from "src/static/icons";

import { StatusColor } from "../constants";
import { getShiftStatusColor } from "../utils";

type ShiftStatus = "Completed" | "Incomplete" | "Checked-in" | "Registered";
type Occupancy = "Full" | "Partial" | "None";

interface ShiftCardProps {
  shiftName: string;
  startTime: string;
  endTime: string;
  /** When set, card shows this as the end of the range (buffered). */
  effectiveEndTime?: string;
  shiftLocation: string;
  category?: string;
  registeredUsers?: number;
  maxUsers?: number;
  onClick: () => void;
  checkedIn: boolean;
  isVolunteer?: boolean;
  now?: moment.Moment;
  attendance?: {
    checkedIn: number;
    missing: number;
  };
}

const ShiftCard: React.FC<ShiftCardProps> = ({
  shiftName,
  startTime,
  endTime,
  effectiveEndTime,
  shiftLocation,
  category,
  registeredUsers,
  maxUsers,
  onClick,
  checkedIn,
  isVolunteer = false,
  now: nowProp,
  attendance,
}) => {
  const formatTime = (time: string) =>
    moment(time).tz("America/New_York").format("h:mm A");

  const displayEnd = effectiveEndTime ?? endTime;
  const startTimeFormatted = formatTime(startTime);
  const endTimeFormatted = formatTime(displayEnd);
  const startDayOfWeek = moment(startTime)
    .tz("America/New_York")
    .format("ddd")
    .toUpperCase();
  const endDayOfWeek = moment(displayEnd)
    .tz("America/New_York")
    .format("ddd")
    .toUpperCase();

  const rangeLabel =
    startDayOfWeek === endDayOfWeek
      ? `${startDayOfWeek} ${startTimeFormatted} - ${endTimeFormatted}`
      : `${startDayOfWeek} ${startTimeFormatted} - ${endDayOfWeek} ${endTimeFormatted}`;

  const now = nowProp ?? moment().tz("America/New_York");
  const isLive = now.isBetween(startTime, displayEnd);
  const isPast = now.isAfter(endTime);

  const getStatus = (): ShiftStatus => {
    if (isPast)
      return isVolunteer
        ? checkedIn
          ? "Completed"
          : "Incomplete"
        : "Completed";
    if (isVolunteer) return checkedIn ? "Checked-in" : "Registered";
    return isLive ? "Checked-in" : "Registered";
  };

  const getOccupancy = (): Occupancy => {
    if (attendance) {
      const total = attendance.checkedIn + attendance.missing;
      if (total === 0 || attendance.checkedIn === total) return "Full";
      if (attendance.checkedIn === 0) return "None";
      return "Partial";
    }
    if (registeredUsers === maxUsers) return "Full";
    if (registeredUsers === 0) return "None";
    return "Partial";
  };

  const status = getStatus();
  const occupancy = getOccupancy();
  const statusColor = getShiftStatusColor(status, occupancy);

  const getStatusText = () => {
    if (isVolunteer || status === "Completed") {
      return status;
    }
    if (isLive && attendance) {
      return `${attendance.checkedIn}/${
        attendance.checkedIn + attendance.missing
      }`;
    }
    return `${registeredUsers}/${maxUsers} ${status}`;
  };

  return (
    <Card onClick={onClick} $isPast={isPast}>
      <Header>
        <TitleContainer>
          {isLive && <LiveIcon />}
          <ShiftName>{shiftName}</ShiftName>
        </TitleContainer>
        <StyledLabel size="sm" color={statusColor}>
          {getStatusText()}
        </StyledLabel>
      </Header>
      <InfoContainer>
        {category && <InfoValue>{category}</InfoValue>}
        <InfoValue>{rangeLabel}</InfoValue>
        {shiftLocation && (
          <LocationRow>
            <LocationIcon aria-hidden />
            <InfoValue>{shiftLocation}</InfoValue>
          </LocationRow>
        )}
      </InfoContainer>
    </Card>
  );
};

const Card = styled.div<{ $isPast: boolean }>`
  display: flex;
  width: 100%;
  padding: 16px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid var(--North-Border-Gray, #d1d5db);
  background: ${({ $isPast }) =>
    $isPast ? "#f9fafb" : "var(--North-Background-White, #fff)"};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9fafb;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const TitleContainer = styled.div`
  display: flex;
  max-width: 50%;
  align-items: center;
  gap: 8px;
`;

const ShiftName = styled.h2`
  color: var(--North-Text-Body, #1f2937);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 160%;
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledLabel = styled(Label)<{ color: StatusColor }>`
  font-size: 12px;
  padding: 2px 8px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const LocationRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const LocationIcon = styled(LocationMarker)`
  flex: none;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const InfoValue = styled.p`
  color: var(--North-Text-Body, #1f2937);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  margin: 0;
`;

export default ShiftCard;
